import React from "react";
import type { ListRange, VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import clsx from "clsx";

import useStyles from "./InfiniteList.styles";

export interface InfiniteListProps<T> {
  data: T[];
  renderItem(index: number, data: T): React.ReactNode;
  totalItems?: number;
  endReached?: (index: number) => void;
  EmptyPlaceholder?: React.ComponentType;
  Footer?: React.ComponentType;
  className?: string;
  scrollbarOnHover?: boolean;
  scrollToIndex?: number;
  rangeChanged?: (range: ListRange) => void;
}

function InfiniteList<T>({
  data,
  endReached,
  renderItem,
  totalItems,
  EmptyPlaceholder,
  Footer,
  className,
  scrollbarOnHover,
  scrollToIndex,
  rangeChanged = () => {},
}: InfiniteListProps<T>): React.ReactElement {
  const classes = useStyles();

  const virtuoso = React.useRef<VirtuosoHandle>(null);

  React.useEffect(() => {
    if (scrollToIndex !== undefined && scrollToIndex >= 0 && scrollToIndex < data.length) {
      virtuoso.current?.scrollToIndex({ index: scrollToIndex, align: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIndex]);

  return (
    <Virtuoso
      ref={virtuoso}
      className={clsx(className, classes.list, scrollbarOnHover && classes.listHover)}
      totalCount={totalItems}
      data={data}
      endReached={endReached}
      itemContent={renderItem}
      rangeChanged={rangeChanged}
      components={{
        EmptyPlaceholder,
        Footer,
      }}
    />
  );
}

export default InfiniteList;
